import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { getToken } from '../../../../context/auth';
import './AIChatModal.css';

class AIChatModal extends Component {
    state = {
        prompt: '',
        generatedHtml: '',
        isStreaming: false,
        activeTab: 'preview', // 'preview' | 'code'
        copied: false,
        error: '',
        chatHistory: [],
        selectedSkillIds: [],

        // Modern multi-selections (rendered once in options card)
        globalTextTypeIds: [],
        globalDimensionComponentIds: [],
        globalElementIds: [],

        paragraphCount: 3,
        wordCount: 200,

        // Tree select state for skills
        showSkillTree: false,
        skillSearchTerm: '',
        expandedCategories: []
    };

    treeSelectRef = React.createRef();
    abortController = null;

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        if (this.props.show) {
            this.setDefaultSelectionsFromQuestion();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.show && this.props.show) {
            this.setDefaultSelectionsFromQuestion();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    handleClickOutside = (event) => {
        if (this.treeSelectRef.current && !this.treeSelectRef.current.contains(event.target)) {
            this.setState({ showSkillTree: false });
        }
    };

    setDefaultSelectionsFromQuestion = () => {
        const { TopicIds, skillDimensionsMap } = this.props;

        let defaultSkillIds = Array.isArray(TopicIds) ? [...TopicIds] : [];
        let defaultTT = [];
        let defaultDC = [];
        let defaultEL = [];

        if (skillDimensionsMap && typeof skillDimensionsMap === 'object') {
            Object.keys(skillDimensionsMap).forEach(topicId => {
                const dims = skillDimensionsMap[topicId] || {};
                if (dims.TextTypeId && !defaultTT.includes(dims.TextTypeId)) defaultTT.push(dims.TextTypeId);
                if (dims.DimensionComponentId && !defaultDC.includes(dims.DimensionComponentId)) defaultDC.push(dims.DimensionComponentId);
                if (dims.ElementId && !defaultEL.includes(dims.ElementId)) defaultEL.push(dims.ElementId);
            });
        }

        this.setState({
            selectedSkillIds: defaultSkillIds,
            globalTextTypeIds: defaultTT,
            globalDimensionComponentIds: defaultDC,
            globalElementIds: defaultEL,
            chatHistory: [],
            generatedHtml: '',
            prompt: ''
        });
    };

    handleSkillToggle = (topicId) => {
        let current = [...this.state.selectedSkillIds];
        if (current.includes(topicId)) {
            current = current.filter(id => id !== topicId);
        } else {
            current.push(topicId);
        }
        this.setState({ selectedSkillIds: current });
    };

    handleGlobalDimensionToggle = (field, value) => {
        this.setState(prevState => {
            const currentArr = Array.isArray(prevState[field]) ? [...prevState[field]] : [];
            const valStr = String(value);
            const exists = currentArr.map(String).includes(valStr);

            let newArr;
            if (exists) {
                newArr = currentArr.filter(x => String(x) !== valStr);
            } else {
                newArr = [...currentArr, value];
            }

            return {
                [field]: newArr
            };
        });
    };

    getSelectedSkillDetailsFormatted = () => {
        const { selectedSkillIds, globalTextTypeIds, globalDimensionComponentIds, globalElementIds } = this.state;
        const { topicsCategories, textTypesList, dimensionComponentsList, elementsList, t } = this.props;

        const rawCategories = Array.isArray(topicsCategories)
            ? topicsCategories
            : (Array.isArray(topicsCategories?.data) ? topicsCategories.data : []);

        const details = [];

        // Selected skills
        if (selectedSkillIds.length > 0) {
            const skillNames = [];
            rawCategories.forEach(cat => {
                if (cat && Array.isArray(cat.Topics)) {
                    cat.Topics.forEach(topic => {
                        if (selectedSkillIds.includes(topic.TopicId)) {
                            skillNames.push(topic.TopicName);
                        }
                    });
                }
            });
            if (skillNames.length > 0) {
                details.push(`• ${(t && t('AILessonChat.selectedSkills')) || 'المهارات المختارة'}: ${skillNames.join('، ')}`);
            }
        }

        // Global dimensions & elements
        const ttNames = (textTypesList || []).filter(x => (globalTextTypeIds || []).map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);
        const dcNames = (dimensionComponentsList || []).filter(x => (globalDimensionComponentIds || []).map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);
        const elNames = (elementsList || []).filter(x => (globalElementIds || []).map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);

        if (ttNames.length > 0) details.push(`• ${(t && t('AILessonChat.textTypes')) || 'أنواع النصوص'}: ${ttNames.join('، ')}`);
        if (dcNames.length > 0) details.push(`• ${(t && t('AILessonChat.textFamilies')) || 'عائلات النصوص / المجالات'}: ${dcNames.join('، ')}`);
        if (elNames.length > 0) details.push(`• ${(t && t('AILessonChat.elements')) || 'العناصر'}: ${elNames.join('، ')}`);

        return details.join('\n');
    };

    toggleCategory = (categoryId) => {
        const expanded = [...this.state.expandedCategories];
        if (expanded.includes(categoryId)) {
            this.setState({ expandedCategories: expanded.filter(id => id !== categoryId) });
        } else {
            expanded.push(categoryId);
            this.setState({ expandedCategories: expanded });
        }
    };

    isCategoryExpanded = (categoryId) => {
        if (this.state.skillSearchTerm) return true;
        return this.state.expandedCategories.includes(categoryId);
    };

    cleanMarkdownHtml = (rawText) => {
        if (!rawText) return '';
        let cleaned = rawText;
        cleaned = cleaned.replace(/```html/gi, '').replace(/```/g, '');
        return cleaned.trim();
    };

    handleSend = async () => {
        const {
            prompt,
            isStreaming,
            chatHistory,
            paragraphCount,
            wordCount
        } = this.state;
        const { t } = this.props;

        if (!prompt || !prompt.trim() || isStreaming) return;

        const token = getToken();
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        const chatEndpoint = `${baseUrl}/ailessonchat`;

        const userMessage = prompt.trim();
        const isSubChat = chatHistory.length > 0;
        const skillDetailsFormatted = this.getSelectedSkillDetailsFormatted();

        this.setState({
            isStreaming: true,
            generatedHtml: '',
            error: '',
            copied: false,
            chatHistory: [...chatHistory, { role: 'user', content: userMessage }]
        });

        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        try {
            const response = await fetch(chatEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    Question: userMessage,
                    SkillName: skillDetailsFormatted,
                    ParagraphCount: parseInt(paragraphCount) || 3,
                    WordCount: parseInt(wordCount) || 200,
                    PassCash: true,
                    Model: 'deepseek',
                    History: chatHistory.map(h => ({ Question: h.content, Answer: h.content })),
                    IsSubChat: isSubChat
                }),
                signal: this.abortController.signal
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error((t && t('AILessonChat.unauthorizedError')) || 'غير مصرح لك بالوصول - صياغة نصوص الدروس متاحة للمشرفين فقط.');
                }
                throw new Error(`خطأ في الاتصال بالخادم (${response.status})`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let accumulatedText = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    let jsonStr = trimmed;
                    if (trimmed.startsWith('data:')) {
                        jsonStr = trimmed.slice(5).trim();
                    }

                    if (jsonStr === '[DONE]') continue;

                    try {
                        const parsed = JSON.parse(jsonStr);
                        if (parsed.Answer !== undefined) {
                            accumulatedText += parsed.Answer;
                            const cleanHtml = this.cleanMarkdownHtml(accumulatedText);
                            this.setState({ generatedHtml: cleanHtml });
                        }
                    } catch (e) {
                        // Skip unparseable JSON stream fragments safely
                    }
                }
            }

            this.setState(prevState => ({
                isStreaming: false,
                chatHistory: [...prevState.chatHistory, { role: 'assistant', content: prevState.generatedHtml }]
            }));

        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error streaming AI lesson response:', err);
            this.setState({
                isStreaming: false,
                error: err.message || ((t && t('AILessonChat.streamFailError')) || 'حدث خطأ أثناء صياغة وتنسيق نص الدرس بواسطة الذكاء الاصطناعي.')
            });
        }
    };

    handleCopyHtml = () => {
        const { generatedHtml } = this.state;
        if (!generatedHtml) return;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(generatedHtml);
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        }
    };

    handleInsert = (append = false) => {
        const { generatedHtml } = this.state;
        if (!generatedHtml) return;
        if (this.props.onInsertHtml) {
            this.props.onInsertHtml(generatedHtml, append);
        }
        if (this.props.onHide) {
            this.props.onHide();
        }
    };

    // Modern Multi-Select List Component with Search, Checkboxes, and Count Summary Trigger
    renderModernMultiSelectList = (title, icon, list, selectedIds, onToggle, instanceKey) => {
        const { t } = this.props;
        const isOpenKey = `openList_${instanceKey}`;
        const searchKey = `searchList_${instanceKey}`;
        const isOpen = this.state[isOpenKey] || false;
        const searchTerm = this.state[searchKey] || '';

        const filtered = (list || []).filter(item =>
            item.CategoryName.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const selectedNames = (list || [])
            .filter(x => selectedIds.map(String).includes(String(x.CategoryId)))
            .map(x => x.CategoryName);

        return (
            <div className="modern-multi-select-container mb-2 w-100">
                <label className="small text-muted mb-1 d-block font-weight-bold">
                    <i className={`${icon} ml-1 text-primary`}></i>
                    {title}:
                    {selectedIds.length > 0 && (
                        <span className="badge badge-primary ml-1 font-weight-bold" style={{ fontSize: '0.75rem', borderRadius: '10px' }}>
                            {selectedIds.length} {(t && t('AILessonChat.selected')) || 'مختار'}
                        </span>
                    )}
                </label>
                <div className="modern-tree-select-wrapper w-100">
                    <div
                        className="modern-tree-select-trigger"
                        onClick={() => this.setState({ [isOpenKey]: !isOpen })}
                        style={{ minHeight: '40px', padding: '8px 12px' }}
                    >
                        <div className="modern-tree-select-values" style={{ fontSize: '0.85rem' }}>
                            {selectedNames.length > 0
                                ? selectedNames.join('، ')
                                : `${(t && t('AILessonChat.selectPrompt')) || 'اختر'} ${title}...`}
                        </div>
                        <i className={`fad fa-chevron-down arrow-icon ${isOpen ? 'open' : ''}`}></i>
                    </div>

                    {isOpen && (
                        <div className="modern-tree-select-dropdown p-2" style={{ zIndex: 1060 }}>
                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                <div className="modern-tree-search-wrapper mb-0 flex-grow-1 ml-2" style={{ marginBottom: 0 }}>
                                    <input
                                        type="text"
                                        className="form-control modern-search-input"
                                        placeholder={`${(t && t('AILessonChat.searchIn')) || 'بحث في'} ${title}...`}
                                        value={searchTerm}
                                        onChange={e => this.setState({ [searchKey]: e.target.value })}
                                        onClick={e => e.stopPropagation()}
                                        style={{ height: '34px', fontSize: '0.82rem' }}
                                    />
                                    <i className="fal fa-search search-icon" style={{ fontSize: '0.85rem' }}></i>
                                </div>
                            </div>

                            <div className="modern-select-list-items" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {filtered.length > 0 ? (
                                    filtered.map(item => {
                                        const isChecked = selectedIds.map(String).includes(String(item.CategoryId));
                                        return (
                                            <label
                                                key={item.CategoryId}
                                                className={`modern-tree-item py-1 px-2 rounded cursor-pointer d-flex align-items-center justify-content-between mb-1 ${isChecked ? 'bg-light font-weight-bold text-primary' : ''}`}
                                                onClick={e => e.stopPropagation()}
                                                style={{ fontSize: '0.84rem' }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => onToggle(item.CategoryId)}
                                                    />
                                                    <span className="checkbox-custom ml-1"></span>
                                                    <span className="topic-name ml-1">{item.CategoryName}</span>
                                                </div>
                                                {isChecked && <i className="far fa-check text-primary"></i>}
                                            </label>
                                        );
                                    })
                                ) : (
                                    <div className="text-muted text-center py-2 small">
                                        {(t && t('AILessonChat.noMatchingResults')) || 'لا توجد نتائج مطابقة'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { show, onHide, topicsCategories, textTypesList, dimensionComponentsList, elementsList, t } = this.props;
        const {
            prompt,
            generatedHtml,
            isStreaming,
            activeTab,
            copied,
            error,
            chatHistory,
            selectedSkillIds,
            globalTextTypeIds,
            globalDimensionComponentIds,
            globalElementIds,
            paragraphCount,
            wordCount,
            showSkillTree,
            skillSearchTerm
        } = this.state;

        const isSubChat = chatHistory.length > 0;

        const rawCategories = Array.isArray(topicsCategories)
            ? topicsCategories
            : (Array.isArray(topicsCategories?.data) ? topicsCategories.data : []);

        const filteredCategories = rawCategories.map(cat => {
            const matchedTopics = (cat.Topics || []).filter(topic =>
                topic.TopicName.toLowerCase().includes(skillSearchTerm.toLowerCase())
            );
            return { ...cat, Topics: matchedTopics };
        }).filter(cat => cat.Topics.length > 0);

        return (
            <Modal
                show={show}
                onHide={onHide}
                className="ai-chat-modal"
                backdrop="static"
                keyboard={false}
                centered
            >
                <div className="ai-chat-modal-header">
                    <h5 className="ai-chat-modal-title">
                        <i className="fal fa-sparkles"></i>
                        {(t && t('AILessonChat.modalTitle')) || 'شات صياغة وتنسيق نصوص الدروس (خاص بالمشرفين)'}
                    </h5>
                    <button className="ai-chat-modal-close" onClick={onHide} title="إغلاق">
                        <i className="fal fa-times"></i>
                    </button>
                </div>

                <div className="ai-chat-modal-body modern-info-container">
                    {/* Admin Lesson Criteria Options Card */}
                    {!isSubChat && (
                        <div className="ai-options-card">
                            <div className="ai-options-title">
                                <i className="fal fa-sliders-h"></i>
                                {(t && t('AILessonChat.optionsTitle')) || 'معايير خيارات الدرس والقوائم المنسدلة'}
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6 mb-2">
                                    <label className="ai-field-label">
                                        {(t && t('AILessonChat.paragraphCount')) || 'عدد الفقرات (افتراضي: 3):'}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        className="ai-input-num w-100"
                                        value={paragraphCount}
                                        onChange={(e) => this.setState({ paragraphCount: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 mb-2">
                                    <label className="ai-field-label">
                                        {(t && t('AILessonChat.wordCount')) || 'عدد الكلمات (افتراضي: 200):'}
                                    </label>
                                    <input
                                        type="number"
                                        min="50"
                                        max="3000"
                                        className="ai-input-num w-100"
                                        value={wordCount}
                                        onChange={(e) => this.setState({ wordCount: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Modern Multi-Select Lists (Rendered ONCE in the options card) */}
                            <div className="row mb-3">
                                <div className="col-md-4 mb-2">
                                    {this.renderModernMultiSelectList(
                                        (t && t('AILessonChat.textTypes')) || 'أنواع النصوص',
                                        'fal fa-file-alt',
                                        textTypesList,
                                        globalTextTypeIds,
                                        (val) => this.handleGlobalDimensionToggle('globalTextTypeIds', val),
                                        'global_tt'
                                    )}
                                </div>
                                <div className="col-md-4 mb-2">
                                    {this.renderModernMultiSelectList(
                                        (t && t('AILessonChat.textFamilies')) || 'عائلات النصوص / المجالات',
                                        'fal fa-puzzle-piece',
                                        dimensionComponentsList,
                                        globalDimensionComponentIds,
                                        (val) => this.handleGlobalDimensionToggle('globalDimensionComponentIds', val),
                                        'global_dc'
                                    )}
                                </div>
                                <div className="col-md-4 mb-2">
                                    {this.renderModernMultiSelectList(
                                        (t && t('AILessonChat.elements')) || 'العناصر',
                                        'fal fa-cube',
                                        elementsList,
                                        globalElementIds,
                                        (val) => this.handleGlobalDimensionToggle('globalElementIds', val),
                                        'global_el'
                                    )}
                                </div>
                            </div>

                            {/* Skill Tree Select Dropdown */}
                            <div className="ai-field-group mb-3">
                                <label className="small text-muted d-block font-weight-bold">
                                    {(t && t('AILessonChat.skillTreeLabel')) || 'اختيار المهارات (شجرة المهارات):'}
                                    {selectedSkillIds.length > 0 && (
                                        <span className="badge badge-primary ml-1 font-weight-bold" style={{ fontSize: '0.75rem', borderRadius: '10px' }}>
                                            {selectedSkillIds.length} {(t && t('AILessonChat.selected')) || 'مختار'}
                                        </span>
                                    )}
                                </label>
                                <div className="modern-tree-select-wrapper w-100" ref={this.treeSelectRef}>
                                    <div
                                        className="modern-tree-select-trigger"
                                        onClick={() => this.setState({ showSkillTree: !showSkillTree })}
                                    >
                                        <div className="modern-tree-select-values">
                                            {selectedSkillIds.length > 0
                                                ? `${(t && t('AILessonChat.selectedSkills')) || 'المهارات المختارة'} (${selectedSkillIds.length})`
                                                : ((t && t('AILessonChat.selectSkillsFromTree')) || 'اختر المهارات من الشجرة...')}
                                        </div>
                                        <i className={`fad fa-chevron-down arrow-icon ${showSkillTree ? 'open' : ''}`}></i>
                                    </div>
                                    {showSkillTree && (
                                        <div className="modern-tree-select-dropdown" style={{ zIndex: 1050 }}>
                                            <div className="modern-tree-search-wrapper">
                                                <input
                                                    type="text"
                                                    className="form-control modern-search-input"
                                                    placeholder={(t && t('AILessonChat.searchSkills')) || 'بحث في المهارات...'}
                                                    value={skillSearchTerm}
                                                    onChange={e => this.setState({ skillSearchTerm: e.target.value })}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                                <i className="fal fa-search search-icon"></i>
                                            </div>
                                            <div className="modern-tree-nodes">
                                                {filteredCategories.map(cat => (
                                                    <div key={cat.CategoryId} className="modern-tree-category">
                                                        <div className="modern-category-header" onClick={() => this.toggleCategory(cat.CategoryId)}>
                                                            <i className={`far ${this.isCategoryExpanded(cat.CategoryId) ? 'fa-folder-open' : 'fa-folder'} category-folder-icon`}></i>
                                                            <span className="category-name">{cat.CategoryName}</span>
                                                            <span className="category-badge">{cat.Topics.length}</span>
                                                            <i className={`fal fa-chevron-down category-chevron ${this.isCategoryExpanded(cat.CategoryId) ? 'expanded' : ''}`}></i>
                                                        </div>
                                                        {this.isCategoryExpanded(cat.CategoryId) && (
                                                            <div className="modern-category-children">
                                                                {cat.Topics.map(topic => (
                                                                    <label
                                                                        key={topic.TopicId}
                                                                        className="modern-tree-item"
                                                                        onClick={e => e.stopPropagation()}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedSkillIds.includes(topic.TopicId)}
                                                                            onChange={() => this.handleSkillToggle(topic.TopicId)}
                                                                        />
                                                                        <span className="checkbox-custom"></span>
                                                                        <span className="topic-name">{topic.TopicName}</span>
                                                                        {topic.QuestionCount > 0 && (
                                                                            <span className="topic-question-count">({topic.QuestionCount})</span>
                                                                        )}
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {isSubChat && (
                        <div className="ai-subchat-notice">
                            <i className="fal fa-comments-alt"></i>
                            {(t && t('AILessonChat.subChatNotice')) || 'يمكنك الآن طلب أي تعديل، إضافة/حذف فقرات، تغيير الألوان، أو إصلاح أي مشاكل في النتيجة المولدة.'}
                        </div>
                    )}

                    {/* Prompt Input Box */}
                    <div className="ai-chat-input-group">
                        <textarea
                            className="ai-chat-textarea"
                            placeholder={
                                isSubChat
                                    ? ((t && t('AILessonChat.textareaPlaceholderSub')) || 'اطلب التعديل أو الإصلاح المطلوب على كود الدرس...')
                                    : ((t && t('AILessonChat.textareaPlaceholderInitial')) || 'اكتب عنوان أو موضوع الدرس، أو ألصق النص المراد صياغته وتنسيقه بـ HTML...')
                            }
                            value={prompt}
                            onChange={(e) => this.setState({ prompt: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    this.handleSend();
                                }
                            }}
                            disabled={isStreaming}
                        />
                        <button
                            className="ai-send-btn w-auto"
                            style={{ width: 'auto' }}
                            onClick={this.handleSend}
                            disabled={!prompt.trim() || isStreaming}
                        >
                            {isStreaming ? (
                                <>
                                    <div className="ai-spinner"></div>
                                    {(t && t('AILessonChat.processing')) || 'جاري المعالجة...'}
                                </>
                            ) : (
                                <>
                                    <i className="fal fa-paper-plane"></i>
                                    {isSubChat
                                        ? ((t && t('AILessonChat.btnUpdate')) || 'تعديل النتيجة')
                                        : ((t && t('AILessonChat.btnGenerate')) || 'إنشاء وتنسيق')}
                                </>
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3">
                            <i className="fal fa-exclamation-triangle ml-2"></i>
                            {error}
                        </div>
                    )}

                    {/* Result Output Box */}
                    {(generatedHtml || isStreaming) && (
                        <div className="ai-result-card mt-3">
                            <div className="ai-result-header">
                                <div className="ai-result-tabs">
                                    <button
                                        className={`ai-tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
                                        onClick={() => this.setState({ activeTab: 'preview' })}
                                    >
                                        <i className="fal fa-eye ml-1"></i>
                                        {(t && t('AILessonChat.previewTab')) || 'معاينة نص الدرس'}
                                    </button>
                                    <button
                                        className={`ai-tab-btn ${activeTab === 'code' ? 'active' : ''}`}
                                        onClick={() => this.setState({ activeTab: 'code' })}
                                    >
                                        <i className="fal fa-code ml-1"></i>
                                        {(t && t('AILessonChat.codeTab')) || 'تعديل كود HTML'}
                                    </button>
                                </div>

                                {isStreaming && (
                                    <div className="ai-streaming-status">
                                        <div className="ai-spinner"></div>
                                        <span>{(t && t('AILessonChat.statusGenerating')) || 'جاري معالجة وتوليد نص الدرس...'}</span>
                                    </div>
                                )}
                            </div>

                            <div className="ai-result-body">
                                {activeTab === 'preview' ? (
                                    <div
                                        className="ai-html-preview"
                                        dangerouslySetInnerHTML={{ __html: generatedHtml }}
                                    />
                                ) : (
                                    <textarea
                                        className="ai-html-editor"
                                        value={generatedHtml}
                                        onChange={(e) => this.setState({ generatedHtml: e.target.value })}
                                        placeholder="كود HTML المولد لنص الدرس يمكن تعديله مباشرة..."
                                    />
                                )}
                            </div>

                            {/* Finishing Action Buttons */}
                            <div className="ai-actions-bar">
                                <button
                                    className="btn-ai-secondary"
                                    onClick={this.handleCopyHtml}
                                    title="نسخ كود HTML"
                                >
                                    <i className={`fal ${copied ? 'fa-check text-success' : 'fa-copy'}`}></i>
                                    {copied
                                        ? ((t && t('AILessonChat.btnCopied')) || 'تم النسخ!')
                                        : ((t && t('AILessonChat.btnCopy')) || 'نسخ HTML')}
                                </button>

                                <button
                                    className="btn-ai-append"
                                    onClick={() => this.handleInsert(true)}
                                    title="إضافة النتيجة لنهاية نص الدرس الحالي"
                                >
                                    <i className="fal fa-plus-circle"></i>
                                    {(t && t('AILessonChat.btnAppend')) || 'إلحاق بنص الدرس'}
                                </button>

                                <button
                                    className="btn-ai-insert"
                                    onClick={() => this.handleInsert(false)}
                                    title="استبدال نص الدرس الحالي بالنتيجة المولدة"
                                >
                                    <i className="fal fa-file-import"></i>
                                    {(t && t('AILessonChat.btnInsert')) || 'إدراج في نص الدرس'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

export default withTranslation('common')(AIChatModal);
